'use client';

import React, { FC } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { Link, usePathname } from '@/i18n/routing';
import logo from '@/public/logo.svg';
import MadaPayment from '@/public/mada-payment-logo.svg';
import VisaPayment from '@/public/visa-payment-logo.svg';
import MasterCardPayment from '@/public/mastercard-payment-logo.svg';
import ApplePayLogo from '@/public/apple=pay-payment-logo.svg';
import InstagramLogo from '@/public/instgram.svg';
import TwitterLogo from '@/public/twitter.svg';
import TikTokLogo from '@/public/tiktok.svg';
import { Routes } from '@/lib/routes/routes';
import useCartStore from '@/lib/state-stores/cartStore';
import SlideUpAnimation from '../animation/SlideUpAnimation';
import { TSession } from '../NavBar';
import LogoIcon from '../icons/Tap/Logo';
import StoreIcon from '../icons/Tap/StoreIcon';
import CartIcon from '../icons/Tap/CartIcon';
import AccountIcon from '../icons/Tap/AccountIcon';
import { trackEvent } from '@/lib/amplitude';
import { AmplitudeEvents } from '@/lib/amplitude/events';
import Container from '../ui/container';

const FooterDesktop = () => {
  const date = new Date().getFullYear();
  const t = useTranslations('about-us');

  return (
    <>
      <footer className="hidden w-full items-center justify-center px-3 py-2 lg:flex">
        <div className="flex w-full max-w-7xl items-center justify-between">
          <div className="flex flex-col">
            <Link
              target="_blank"
              href="https://prod-pik.s3.eu-central-1.amazonaws.com/custom/%D8%B4%D9%87%D8%A7%D8%AF%D8%A9+%D8%AA%D8%B3%D8%AC%D9%8A%D9%84+%D9%85%D8%AC%D9%85%D9%88%D8%B9%D8%A9+%D8%B6%D8%B1%D9%8A%D8%A8%D9%8A%D8%A92.PDF"
            >
              <Image
                src={'/vat-logo.svg'}
                alt="vat-logo"
                width={0}
                height={0}
                className="mb-[9px] h-[41.1px] w-[32px]"
              />
            </Link>
            <Image
              src={logo}
              alt="pik-logo"
              width={100}
              className="w-20"
              onClick={() => trackEvent(AmplitudeEvents.HOME_APP_CLICKED)}
            />

            <p className="text-primary">
              {date} {t('jahez')}
            </p>
          </div>

          <div className="flex flex-col items-center gap-y-[33px]">
            <div className="flex gap-[16px]">
              <Image src={MadaPayment} alt="mada-logo" className="h-[15.24px] w-[45.7px]" />
              <Image src={VisaPayment} alt="visa-logo" className="h-[15.24px] w-[45.7px]" />
              <Image
                src={MasterCardPayment}
                alt="mastercard-logo"
                className="h-[15.24px] w-[45.7px]"
              />
              <Image src={ApplePayLogo} alt="apple-pay-logo" className="h-[15.24px] w-[45.7px]" />
            </div>
            <div className="flex h-full w-full gap-[60px]">
              <Link
                onClick={() => trackEvent(AmplitudeEvents.ABOUT_PIK_CLICKED)}
                href={Routes.aboutUs}
              >
                {t('About PIK')}
              </Link>
              <Link
                onClick={() => trackEvent(AmplitudeEvents.TERMS_AND_CONDITIONS_CLICKED)}
                href={Routes.termsAndCondition}
              >
                {t('TermsConditions')}
              </Link>
              <Link
                onClick={() => trackEvent(AmplitudeEvents.CONTACT_US_CLICKED)}
                href={Routes.contactUs}
              >
                {t('Contact us')}
              </Link>
              <Link
                onClick={() => trackEvent(AmplitudeEvents.CONTACT_US_CLICKED)}
                href={Routes.privacyPolicy}
              >
                {t('Privacy Policy')}
              </Link>
            </div>
          </div>

          <div className="flex flex-col items-center gap-y-[40px]">
            <div className="flex gap-x-[22px]">
              <Link href="https://www.instagram.com/pikappsa" target="_blank">
                <Image
                  src={InstagramLogo}
                  alt="instgram-logo"
                  className="h-6 w-6"
                  width={24}
                  height={24}
                />
              </Link>

              <Link href="https://www.tiktok.com/@pikappsa" target="_blank">
                <Image
                  src={TikTokLogo}
                  alt="tiktok-logo"
                  className="h-6 w-6"
                  width={24}
                  height={24}
                />
              </Link>
              <Link href="https://x.com/pikappsa" target="_blank">
                <Image
                  src={TwitterLogo}
                  alt="twitter-logo"
                  className="h-6 w-6"
                  width={24}
                  height={24}
                />
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

const FooterMobile: FC<TSession> = ({ session }) => {
  const { carts } = useCartStore();
  const t = useTranslations('header');

  const currentPath = usePathname();
  const isActive = (path: string) => currentPath === path;

  const hiddenFrom = (paths: string | string[]) => {
    const pathsArray = Array.isArray(paths) ? paths : [paths];

    return pathsArray.some((path) => currentPath.includes(path));
  };

  const NavItem = ({
    href,
    icon: Icon,
    label,
    onClick,
  }: {
    href: string;
    icon: FC<{ color: string }>;
    label: string;
    onClick?: () => void;
  }) => (
    <div className="relative">
      <Link href={href} prefetch={true} onClick={onClick}>
        <div className="flex flex-col items-center justify-center gap-0.5">
          <Icon color={isActive(href) ? '#1a1a1a' : '#A6A6A6'} />
          <span
            className={`text-[10px] font-medium ${isActive(href) ? 'text-primary font-semibold' : 'text-[#aaaaaa]'}`}
          >
            {label}
          </span>
        </div>
      </Link>
    </div>
  );

  if (hiddenFrom(['checkout'])) return null;

  return (
    <nav className="shadow-menu fixed right-0 bottom-0 left-0 z-50 flex items-center rounded-t-2xl bg-white/80 pt-2 pb-4 backdrop-blur-xl lg:hidden">
      <Container>
        <ul className="flex items-center justify-between font-bold">
          <li>
            <NavItem
              href="/"
              icon={LogoIcon}
              label=""
              onClick={() => trackEvent(AmplitudeEvents.HOME_APP_CLICKED)}
            />
          </li>
          <li>
            <NavItem
              href="/stores"
              icon={StoreIcon}
              label={t('stores')}
              onClick={() => trackEvent(AmplitudeEvents.STORES_CLICKED)}
            />
          </li>
          <li className="relative">
            <NavItem
              href="/carts"
              icon={CartIcon}
              label={t('carts')}
              onClick={() => trackEvent(AmplitudeEvents.CART_CLICKED)}
            />
            {session?.user && carts.length ? (
              <div className="centerButton absolute top-[-10%] left-[50%] grid aspect-square min-w-6 place-items-center rounded-full bg-red-500 text-xs text-white">
                <SlideUpAnimation uniqueKey={carts.length}>{carts.length}</SlideUpAnimation>
              </div>
            ) : null}
          </li>

          <li>
            <NavItem
              href="/account"
              icon={AccountIcon}
              label={t('account')}
              onClick={() => trackEvent(AmplitudeEvents.MY_ACCOUNT_CLICKED)}
            />
          </li>
        </ul>
      </Container>
    </nav>
  );
};

const Footer = ({ session }: TSession) => {
  return (
    <>
      <FooterDesktop />
      <FooterMobile session={session} />
    </>
  );
};

export default Footer;
